/**
 * WorkdayStartSheet.js
 *
 * Compact standalone start-of-day prompt (bottom sheet, same visual language as the briefing). Shown ONLY when the
 * "Mai napom" briefing will not open today (skipped / already seen) but the workday has not started and the prompt was not
 * dismissed today — see {@link module:helpers/workdayRules.startPromptHost}. When the briefing opens it hosts the prompt in its
 * own Munkanap hero card instead, so the two never appear together.
 *
 * @module components/workday/WorkdayStartSheet
 */

import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import {useTranslation} from 'react-i18next';
import {WorkdaySheetShell} from './WorkdayShell';
import {OutlineButton, PrimaryButton, WorkModeSelector} from './WorkdayParts';
import {useManagementTheme} from '../../helpers/managementTheme';
import {useWorkday} from '../../helpers/WorkdayContext';

/**
 * @returns {JSX.Element|null} the start sheet (rendered while `surface === 'start'`).
 */
export default function WorkdayStartSheet() {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {surface, busy, start, dismissStart, skipStartToday} = useWorkday();

    if (surface !== 'start') {
        return null;
    }

    return (
        <WorkdaySheetShell visible onClose={dismissStart}>
            <LinearGradient colors={['#1f2a44', '#243656']} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.hero}>
                <View style={styles.accentBar} />
                <LottieView source={require('../../../assets/animations/workday-clock.json')} autoPlay loop style={styles.lottie} />
            </LinearGradient>
            <View style={styles.body}>
                <Text style={[styles.title, {color: m.colors.heading}]}>{t('workday_start_prompt_title')}</Text>
                <Text style={[styles.text, {color: m.colors.subText}]}>{t('workday_start_prompt_text')}</Text>
                <WorkModeSelector />
                <PrimaryButton label={t('workday_start')} icon="play-circle-outline" busy={busy} onPress={() => start({})} />
                <View style={styles.row}>
                    <OutlineButton flex icon="clock-outline" label={t('workday_start_later')} onPress={dismissStart} tone={m.colors.subText} />
                    <OutlineButton flex icon="eye-off-outline" label={t('workday_start_skip_today')} onPress={skipStartToday} tone={m.colors.subText} />
                </View>
            </View>
        </WorkdaySheetShell>
    );
}

const styles = StyleSheet.create({
    hero: {borderRadius: 24, height: 150, alignItems: 'center', justifyContent: 'center', overflow: 'hidden'},
    accentBar: {position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: '#F59E0B'},
    lottie: {width: 130, height: 130},
    body: {paddingTop: 18, gap: 12},
    title: {fontSize: 22, fontWeight: '900', letterSpacing: -0.4, textAlign: 'center'},
    text: {fontSize: 14, fontWeight: '600', textAlign: 'center', lineHeight: 20, marginBottom: 4},
    row: {flexDirection: 'row', gap: 10},
});
